import {
  BestOfBallotResponse,
  BestOfBallotWrite,
  BestOfCandidateFilters,
  BestOfCandidatePage,
  BestOfContestList,
  BestOfErrorDetails,
  BestOfEvent,
  BestOfHomepageTakeover,
  BestOfFailurePayload,
  BestOfMineSubmissions,
  BestOfMyVotes,
  BestOfLibraryFilters,
  BestOfLibraryEditions,
  BestOfLibraryOverview,
  BestOfLibraryPage,
  BestOfResults,
  BestOfSubmission,
  BestOfSubmissionFailureEnvelope,
  BestOfSubmissionFilters,
  BestOfSubmissionPage,
  BestOfSubmissionWriteResponse,
} from 'app/features/BestOf/types';
import { http } from 'services/http';

interface BestOfHttpResponse<T> {
  ok: boolean;
  data?: T | BestOfFailurePayload | BestOfSubmissionFailureEnvelope;
  status?: number | null;
  originalError?: Error | null;
}

export class BestOfApiError extends Error {
  status: number;
  code?: string;
  details?: BestOfErrorDetails;

  constructor(message: string, status: number, code?: string, details?: BestOfErrorDetails) {
    super(message);
    this.name = 'BestOfApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, BestOfApiError.prototype);
  }
}

export const unwrapBestOfResponse = async <T>(
  request: Promise<BestOfHttpResponse<T>>
): Promise<T> => {
  const response = await request;
  if (response.ok && response.data) return response.data as T;

  const body = response.data as BestOfFailurePayload | BestOfSubmissionFailureEnvelope | undefined;
  const failure = (body && 'error' in body ? body.error : body) as
    | (BestOfFailurePayload & { submission?: BestOfSubmission })
    | undefined;
  let details = failure?.details;
  if (!details && failure?.submission) {
    details = { submission: failure.submission };
  }

  throw new BestOfApiError(
    failure?.message ||
      response.originalError?.message ||
      "We couldn't complete the Best of request. Please try again.",
    response.status || 500,
    failure?.code,
    details
  );
};

export const getBestOfEvent = (year: number) =>
  unwrapBestOfResponse<BestOfEvent>(http.get(`best_of_years/${year}/index`));

export const getBestOfCommentDestination = (
  year: number,
  resourceUuid: string | null,
  messageUuid: string | null,
  entry: { entry_uuid?: string; submission_uuid?: string } = {}
) =>
  unwrapBestOfResponse<{ path: string }>(
    http.get('best_of_years/' + year + '/comment_destination', {
      resource_uuid: resourceUuid,
      message_uuid: messageUuid,
      ...entry,
    })
  );

export const getBestOfHomepageTakeover = () =>
  unwrapBestOfResponse<BestOfHomepageTakeover>(http.get('best_of_years/homepage_takeover'));

export const getBestOfContests = (year: number) =>
  unwrapBestOfResponse<BestOfContestList>(http.get(`best_of_years/${year}/contests`));

export const getBestOfMyVotes = (year: number) =>
  unwrapBestOfResponse<BestOfMyVotes>(http.get(`best_of_years/${year}/my_votes`));

export const getBestOfCandidates = (
  year: number,
  contestUuid: string,
  filters: BestOfCandidateFilters
) =>
  unwrapBestOfResponse<BestOfCandidatePage>(
    http.get(`best_of_years/${year}/contests/${contestUuid}/candidates`, filters)
  );

export const writeBestOfBallot = (year: number, contestUuid: string, ballot: BestOfBallotWrite) =>
  unwrapBestOfResponse<BestOfBallotResponse>(
    http.put(`best_of_years/${year}/contests/${contestUuid}/ballot`, ballot)
  );

export const getBestOfResults = (year: number) =>
  unwrapBestOfResponse<BestOfResults>(http.get(`best_of_years/${year}/results`));

export const getBestOfLibrary = (year: number) =>
  unwrapBestOfResponse<BestOfLibraryOverview>(http.get(`best_of_years/${year}/library`));

export const getBestOfLibraryEditions = () =>
  unwrapBestOfResponse<BestOfLibraryEditions>(http.get('best_of_years/library_editions'));

export const getBestOfLibrarySubmissions = (year: number, filters: BestOfLibraryFilters) =>
  unwrapBestOfResponse<BestOfLibraryPage>(
    http.get<BestOfLibraryPage>(`best_of_years/${year}/library/submissions`, filters)
  );

export const getBestOfSubmissions = (year: number, filters: BestOfSubmissionFilters) =>
  unwrapBestOfResponse<BestOfSubmissionPage | BestOfMineSubmissions>(
    http.get<BestOfSubmissionPage | BestOfMineSubmissions>(
      `best_of_years/${year}/submissions`,
      filters
    )
  );

export const withdrawBestOfSubmission = (
  year: number,
  submissionUuid: string,
  contentVersion: number
) =>
  unwrapBestOfResponse<BestOfSubmissionWriteResponse>(
    http.put<BestOfSubmissionWriteResponse>(
      `best_of_years/${year}/submissions/${submissionUuid}/withdraw`,
      { content_version: contentVersion }
    )
  );

export const resubmitBestOfSubmission = (
  year: number,
  submissionUuid: string,
  contentVersion: number
) =>
  unwrapBestOfResponse<BestOfSubmissionWriteResponse>(
    http.put<BestOfSubmissionWriteResponse>(
      `best_of_years/${year}/submissions/${submissionUuid}/resubmit`,
      { content_version: contentVersion }
    )
  );
